// ============================================================================
// atiratView.js — ÁTIRAT megjelenítése és javítása (2026-10-19, v119; lásd functions/src/transcriptCorrection.js).
//
// Az ÁTIRAT egy idegen nyelvű forrásszöveg (pl. régi újságcikk japán átirata), amelyet a küldő nyelvvel jelölt: ezen a nyelven jelenik meg (a nyelvnek megfelelő
// betűkkel), automatikusan nem fordítódik, de a fordítása a hozzászólás alatt kérhető ("Fordítás kérése"). Az átirat nyelvét beszélő tag (a japán kolléga), a szerző
// és az admin / témafelelős javíthatja; a javítás jelölt ("Átirat javítva: név · idő"), és a régi szövegek előzményben maradnak.
//
//   - renderAtiratSor:  a beviteli sáv fölötti "📜 Átirat" kapcsoló és nyelvválasztó (a következő hozzászólás átiratként megy);
//   - renderAtirat:     a hozzászólás alatt: jelvény, "javítva" jelzés, "✎ Átirat javítása" szerkesztő, "📜 Átirat-előzmény".
// ============================================================================

import { t, localeFor } from "./i18n.js";
import { el } from "./dom.js";

function datum(ms, uiLang) {
  const n = typeof ms === "number" ? ms : ms && typeof ms.toMillis === "function" ? ms.toMillis() : 0;
  if (!n) return "";
  try {
    return new Date(n).toLocaleString(localeFor(uiLang), { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch (e) {
    return new Date(n).toISOString().slice(0, 16);
  }
}

const nevVagyTorolt = (nev, uid, uiLang) => (uid === "deleted" ? t(uiLang, "msg.deletedSender") : nev || "?");

/** Az átirat nyelvének választható listája: a csevegési nyelvek, a saját csevegési nyelv KIVÉVE (az a sima hozzászólás). */
export function atiratNyelvek(chatNyelvek, sajatNyelv) {
  return Object.keys(chatNyelvek || {}).filter((k) => k !== sajatNyelv);
}

/** Az alapértelmezett átirat-nyelv bekapcsoláskor: a japán, ha van és nem a saját nyelv; különben az első választható. */
export function alapAtiratNyelv(chatNyelvek, sajatNyelv) {
  const lista = atiratNyelvek(chatNyelvek, sajatNyelv);
  return lista.includes("ja") ? "ja" : lista[0] || null;
}

/** Javíthatja-e a néző az átiratot? (a szerver is ezt dönti: szerző, admin / témafelelős, vagy az átirat nyelvét beszélő tag) */
export function atiratJavithato(msg, viewerLang, myUid, staff) {
  if (!msg || msg.transcript !== true || msg.deleted === true || !msg.messageId) return false;
  return msg.senderId === myUid || staff === true || (typeof viewerLang === "string" && viewerLang !== "" && viewerLang === msg.sourceLang);
}

/** Modernizálható-e az átirat (japán nyelvű, nem törölt, és a néző írhat a szobába)? A szerver is ezt dönti. */
export function atiratModernizalhato(msg, es) {
  if (!msg || msg.transcript !== true || msg.deleted === true || !msg.messageId) return false;
  if (es && es.irhato === false) return false;
  return typeof msg.sourceLang === "string" && /^ja(-|$)/i.test(msg.sourceLang);
}

/**
 * A beviteli sáv fölötti átirat-sor: kapcsoló + nyelvválasztó + rövid magyarázat.
 * @param {object} model { uiLang, atiratNyelv (null = kikapcsolva), contentLang, chatNyelvek: {kód: név} }
 * @param {object} handlers onAtiratToggle(), onAtiratLang(kod)
 * @returns {HTMLElement|null}
 */
export function renderAtiratSor(model, handlers) {
  const { uiLang, atiratNyelv, contentLang, chatNyelvek } = model;
  const lista = atiratNyelvek(chatNyelvek, contentLang);
  if (lista.length === 0 || !handlers || !handlers.onAtiratToggle) return null;
  const aktiv = typeof atiratNyelv === "string" && atiratNyelv !== "";
  const doboz = el("div", { class: "atirat-sor" + (aktiv ? " active" : "") });
  doboz.appendChild(el("button", {
    type: "button", class: "text-btn atirat-toggle", "aria-pressed": aktiv ? "true" : "false", title: t(uiLang, "atirat.toggleHint"),
    text: "📜 " + t(uiLang, "atirat.toggle"), onclick: handlers.onAtiratToggle,
  }));
  if (aktiv) {
    const valaszto = el("select", { class: "panel-input atirat-lang", "aria-label": t(uiLang, "atirat.langLabel") });
    for (const kod of lista) {
      const nev = typeof chatNyelvek[kod] === "object" && chatNyelvek[kod] !== null ? chatNyelvek[kod].name || kod : chatNyelvek[kod] || kod;
      const o = el("option", { value: kod, text: `${nev} (${kod.toUpperCase()})` });
      if (kod === atiratNyelv) o.selected = true;
      valaszto.appendChild(o);
    }
    valaszto.addEventListener("change", () => handlers.onAtiratLang(valaszto.value));
    doboz.appendChild(el("label", { class: "atirat-lang-label" }, [el("span", { text: t(uiLang, "atirat.langLabel") + ": " }), valaszto]));
    doboz.appendChild(el("div", { class: "hint atirat-hint", text: t(uiLang, "atirat.hint") }));
  }
  return doboz;
}

/**
 * A hozzászólás alatti átirat-rész.
 * @param {object} msg a hozzászólás
 * @param {object} es { atiratSzerk: null | {messageId, text, note, busy, error}, isStaff }
 * @param {object} handlers onAtiratOpen(messageId, text), onAtiratChange(mezo, ertek), onAtiratCancel, onAtiratSave
 * @returns {HTMLElement|null} null, ha a hozzászólás nem átirat
 */
export function renderAtirat(msg, viewerLang, uiLang, myUid, es, handlers) {
  if (!msg || msg.transcript !== true || msg.deleted === true || !msg.messageId) return null;
  const doboz = el("div", { class: "msg-atirat" });
  const sor = el("div", { class: "msg-atirat-row" });
  sor.appendChild(el("span", { class: "msg-atirat-badge", text: "📜 " + t(uiLang, "atirat.badge", { lang: String(msg.sourceLang || "?").toUpperCase() }) }));
  const javit = msg.transcriptCorrected && typeof msg.transcriptCorrected === "object" ? msg.transcriptCorrected : null;
  const szerkeszto = es && es.atiratSzerk && es.atiratSzerk.messageId === msg.messageId ? es.atiratSzerk : null;
  if (!szerkeszto && handlers && handlers.onAtiratOpen && atiratJavithato(msg, viewerLang, myUid, es && es.isStaff)) {
    sor.appendChild(el("button", { type: "button", class: "text-btn msg-atirat-fix", text: "✎ " + t(uiLang, "atirat.fix"), onclick: () => handlers.onAtiratOpen(msg.messageId, msg.text || "") }));
  }
  // Modern írásmódra (v205): japán átiratnál, bármely szobatagnak; az eredmény új AI-hozzászólás (1. szint: kanji / kana / írásjel).
  const modern = es && es.modernAllapot && es.modernAllapot.messageId === msg.messageId ? es.modernAllapot : null;
  if (!szerkeszto && handlers && handlers.onAtiratModernize && atiratModernizalhato(msg, es)) {
    sor.appendChild(el("button", {
      type: "button", class: "text-btn msg-atirat-modern", title: t(uiLang, "atirat.modernizeHint"), disabled: modern && modern.busy === true,
      text: "🪄 " + t(uiLang, modern && modern.busy ? "atirat.modernizing" : "atirat.modernize"), onclick: () => handlers.onAtiratModernize(msg.messageId),
    }));
  }
  doboz.appendChild(sor);
  if (modern && modern.error) doboz.appendChild(el("div", { class: "panel-status err msg-atirat-modern-error", role: "alert", text: modern.error }));

  // "Átirat javítva": ki, mikor, hányadik javítás; a megjegyzés a címkén.
  if (javit) {
    const db = Number.isInteger(javit.count) ? javit.count : 1;
    const szoveg = t(uiLang, "atirat.fixed", { name: nevVagyTorolt(javit.byName, javit.by, uiLang), time: datum(javit.at, uiLang) }) + (db > 1 ? " " + t(uiLang, "atirat.fixedMany", { n: db }) : "");
    doboz.appendChild(el("div", { class: "msg-atirat-fixed", title: javit.note || null, text: "✎ " + szoveg + (javit.note ? ` · ${javit.note}` : "") }));
  }

  // A szerkesztő: a mostani átirat előtöltve (az átirat nyelvén), megjegyzés, mentés / mégse.
  if (szerkeszto) {
    const szerk = el("div", { class: "msg-atirat-editor" });
    szerk.appendChild(el("label", { class: "panel-label small", text: t(uiLang, "atirat.editLabel") }));
    const mezo = el("textarea", { class: "panel-input", rows: "5", lang: msg.sourceLang || null });
    mezo.value = szerkeszto.text || "";
    mezo.addEventListener("input", () => handlers.onAtiratChange("text", mezo.value));
    szerk.appendChild(mezo);
    szerk.appendChild(el("label", { class: "panel-label small", text: t(uiLang, "atirat.noteLabel") }));
    const megj = el("input", { class: "panel-input", type: "text", maxlength: "200" });
    megj.value = szerkeszto.note || "";
    megj.addEventListener("input", () => handlers.onAtiratChange("note", megj.value));
    szerk.appendChild(megj);
    if (szerkeszto.error) szerk.appendChild(el("div", { class: "panel-status err", role: "alert", text: szerkeszto.error }));
    szerk.appendChild(el("div", { class: "panel-row" }, [
      el("button", { type: "button", class: "neutral-btn", text: t(uiLang, "glossary.cancel"), disabled: szerkeszto.busy === true, onclick: handlers.onAtiratCancel }),
      el("button", { type: "button", class: "primary-btn compact", text: szerkeszto.busy ? "…" : t(uiLang, "atirat.save"), disabled: szerkeszto.busy === true, onclick: handlers.onAtiratSave }),
    ]));
    doboz.appendChild(szerk);
  }

  // Átirat-előzmény: a cserélt (régi) szövegek, legújabb elöl; összecsukva.
  const elozmeny = (Array.isArray(msg.transcriptCorrections) ? msg.transcriptCorrections : []).filter((e) => e && typeof e.text === "string").slice().reverse();
  if (elozmeny.length > 0) {
    const reszletek = el("details", { class: "msg-atirat-history" });
    reszletek.appendChild(el("summary", { text: "📜 " + t(uiLang, "atirat.history", { n: elozmeny.length }) }));
    for (const e of elozmeny) {
      reszletek.appendChild(el("div", { class: "msg-atirat-history-row" }, [
        el("div", { class: "msg-review-log-head", text: t(uiLang, "atirat.fixed", { name: nevVagyTorolt(e.byName, e.by, uiLang), time: datum(e.at, uiLang) }) + (e.note ? ` · ${e.note}` : "") }),
        el("div", { class: "msg-review-log-text", lang: msg.sourceLang || null, text: e.text }),
      ]));
    }
    doboz.appendChild(reszletek);
  }
  return doboz;
}
